/**
 * Browser translation tools (Google Translate in Chrome, Safari translation,
 * etc.) rewrite text nodes into <font> wrappers behind React's back. When
 * React later reconciles that DOM it calls removeChild/insertBefore against
 * nodes the translator has already moved, which throws
 * "NotFoundError: Failed to execute 'insertBefore'/'removeChild' on 'Node'"
 * and crashes the whole app to the error boundary (FR-2066).
 *
 * Patch the two methods to degrade gracefully instead of throwing, so
 * patients keep the ability to auto-translate the app. Standard workaround
 * from https://github.com/facebook/react/issues/11538#issuecomment-417504600
 */
export function applyDomSafetyPatch() {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      console.warn(
        'domSafetyPatch: skipped removeChild of a node whose parent changed (browser translation active?)'
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn(
        'domSafetyPatch: skipped insertBefore with a detached reference node (browser translation active?)'
      );
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
